import React,{useState,useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {supabase} from '../../supabaseClient';
import SafeIcon from '../../common/SafeIcon';
import {TEAM_K9_LOGO_URL,FALLBACK_LOGO_URL} from '../../common/assets';

const OwnerPortalEntry=()=> {
  const navigate=useNavigate();
  const [formData,setFormData]=useState({ownerName: '',dogName: ''});
  const [loading,setLoading]=useState(false);
  const [error,setError]=useState(null);
  const [matches,setMatches]=useState([]);
  const [logoError,setLogoError]=useState(false);
  const [trainer,setTrainer]=useState(null);

  useEffect(()=> {
    const fetchTrainerInfo=async ()=> {
      try {
        const {data,error}=await supabase
          .from('trainer_profiles')
          .select('signup_url,business_name')
          .order('updated_at',{ascending: false})
          .limit(1)
          .single();
        
        if (!error && data) {
          setTrainer(data);
        }
      } catch (err) {
        console.error('Error fetching trainer info:',err);
      }
    };
    fetchTrainerInfo();
  },[]);

  const handleSearch=async (e)=> {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setMatches([]);
    try {
      const {data: clients,error: clientError}=await supabase
        .from('training_clients')
        .select('id,owner_name,dog_name')
        .ilike('owner_name',formData.ownerName.trim())
        .ilike('dog_name',formData.dogName.trim());

      if (clientError) throw clientError;

      if (!clients || clients.length===0) {
        setError("We couldn't find a training plan for that owner and dog. Please check the spelling or contact your trainer.");
        setLoading(false);
        return;
      }

      const clientIds=clients.map(c=> c.id);
      const {data: plans,error: planError}=await supabase
        .from('training_plans')
        .select('id,client_id,plan_name,start_date')
        .in('client_id',clientIds)
        .order('start_date',{ascending: false});

      if (planError) throw planError;

      if (!plans || plans.length===0) {
        setError("We couldn't find any training plans for that owner and dog.");
        setLoading(false);
        return;
      }

      const validMatches=plans.map(plan=> {
        const client=clients.find(c=> c.id===plan.client_id);
        return {...plan,...client};
      });

      if (validMatches.length===1) {
        navigate(`/portal/plan/${validMatches[0].id}`);
      } else {
        setMatches(validMatches);
      }
    } catch (err) {
      setError("An error occurred while searching for your plan.");
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpClick=()=> {
    if (trainer?.signup_url) {
      window.location.href=trainer.signup_url;
    } else {
      alert("Signup is not available yet. Please contact Team K9 Training.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-1 flex flex-col items-center justify-center p-4 sm:p-6 lg:p-8">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img 
              src={logoError ? FALLBACK_LOGO_URL : TEAM_K9_LOGO_URL} 
              alt="Team K9 Logo" 
              className="w-32 h-32 sm:w-36 sm:h-36 mx-auto mb-6 object-contain" 
              onError={()=> setLogoError(true)} 
            />
            <h1 className="text-3xl sm:text-4xl font-black text-gray-900 mb-2 tracking-tight">Owner Portal</h1>
            <p className="text-gray-500 text-base sm:text-lg font-medium">Access your personalized training plan</p>
          </div>

          <div className="bg-white rounded-[2.5rem] shadow-2xl shadow-gray-200 border border-gray-100 p-6 sm:p-8 overflow-hidden">
            {matches.length > 0 ? (
              <div className="space-y-4">
                <h2 className="text-lg font-black text-gray-800 mb-2">Select your plan:</h2>
                {matches.map(match=> (
                  <button 
                    key={match.id} 
                    onClick={()=> navigate(`/portal/plan/${match.id}`)}
                    className="w-full text-left p-5 rounded-2xl border-2 border-gray-50 hover:border-indigo-500 hover:bg-indigo-50 transition-all group"
                  >
                    <div className="font-black text-gray-900 group-hover:text-indigo-700">{match.dog_name} & {match.owner_name}</div>
                    <div className="text-xs text-gray-500 mt-1 font-bold uppercase tracking-widest">{match.plan_name}</div>
                  </button>
                ))}
                <button onClick={()=> setMatches([])} className="w-full text-center text-sm font-bold text-gray-400 mt-4 hover:text-indigo-600">Back to search</button>
              </div>
            ) : (
              <form onSubmit={handleSearch} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Owner Name</label>
                  <input 
                    required 
                    value={formData.ownerName} 
                    onChange={(e)=> setFormData({...formData,ownerName: e.target.value})}
                    placeholder="e.g. John Smith" 
                    className="w-full px-5 py-4 rounded-2xl border-2 border-gray-50 focus:border-indigo-500 focus:bg-white transition-all bg-gray-50 outline-none font-bold text-gray-900" 
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Dog Name</label>
                  <input 
                    required 
                    value={formData.dogName} 
                    onChange={(e)=> setFormData({...formData,dogName: e.target.value})}
                    placeholder="e.g. Max" 
                    className="w-full px-5 py-4 rounded-2xl border-2 border-gray-50 focus:border-indigo-500 focus:bg-white transition-all bg-gray-50 outline-none font-bold text-gray-900" 
                  />
                </div>

                {error && <div className="p-4 rounded-2xl bg-red-50 text-red-600 text-xs font-bold border border-red-100">{error}</div>}

                <button 
                  type="submit" 
                  disabled={loading}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black py-4 sm:py-5 rounded-2xl shadow-xl shadow-indigo-100 transition-all flex items-center justify-center gap-3"
                >
                  {loading ? (
                    <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <><span>View Plan</span><SafeIcon name="ArrowRight" /></>
                  )}
                </button>
              </form>
            )}
          </div>

          <div className="mt-10 text-center">
            <div className="inline-flex flex-col items-center p-8 bg-indigo-50/50 rounded-[2.5rem] border border-indigo-100/50 backdrop-blur-sm">
              <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center shadow-sm mb-4">
                <SafeIcon name="Award" className="text-indigo-600 w-6 h-6" />
              </div>
              <p className="text-xs text-indigo-400 font-black uppercase tracking-[0.2em] mb-2">New Here?</p>
              <p className="text-gray-900 font-black text-xl mb-4">Ready to transform your dog?</p>
              <button 
                onClick={handleSignUpClick}
                className="flex items-center gap-3 bg-white px-8 py-4 rounded-2xl text-indigo-600 font-black text-sm shadow-md hover:shadow-lg hover:scale-105 transition-all border border-indigo-100/50"
              >
                Sign Up Now <SafeIcon name="ArrowRight" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerPortalEntry;